import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import Layout from "../../components/ui/Layout";
import NotFound from "../NotFound";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useMdxContent from "../../hooks/useMdxContent";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const isVideoUrl = (url = "") => {
  const cleanUrl = url.split("?")[0].split("#")[0].toLowerCase();
  return cleanUrl.endsWith(".mp4");
};

export default function EventDetails() {
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { eventId } = useParams();
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_BACKEND_URI +
            "events/getEvent?eventId=" +
            eventId,
        );
        if (!response.ok) {
          if (response.status === 404) throw new Error("Event not found");
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setEventData(data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    if (eventId) fetchEventData();
  }, [eventId]);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#0e0e0e] z-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border border-white/20 border-t-white/80 rounded-full animate-spin" />
          <span
            className="text-white/40 text-xs tracking-[0.3em] uppercase"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Caricamento
          </span>
        </div>
      </div>
    );
  }

  if (error) return <NotFound />;
  if (!eventData) return null;

  const heroMedia = eventData.eventImagesUrls?.[0]?.startsWith("http")
    ? eventData.eventImagesUrls[0]
    : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733332084/${eventData.eventImagesUrls?.[0]}`;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500&display=swap');

        .event-page { font-family: 'Outfit', sans-serif; }
        .serif { font-family: 'Cormorant Garamond', serif; }

        .hero-badge {
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.15);
        }

        .detail-card {
          background: #faf9f7;
          border: 1px solid rgba(0,0,0,0.07);
        }

        .divider-line {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(64,101,128,0.4), transparent);
        }

        .gallery-item img, .gallery-item video {
          transition: transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .gallery-item:hover img, .gallery-item:hover video {
          transform: scale(1.06);
        }

        .back-btn {
          border: 1px solid rgba(64,101,128,0.3);
          transition: all 0.3s ease;
        }
        .back-btn:hover {
          background: #406580;
          border-color: #406580;
          color: white;
        }

        .chapter-number {
          font-size: 6rem;
          line-height: 1;
          color: rgba(64,101,128,0.08);
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          position: absolute;
          top: -1.5rem;
          left: -1rem;
          pointer-events: none;
          user-select: none;
        }

        /* MDX rendered content styles */
        .mdx-content p { margin-bottom: 0.9rem; font-size: 0.92rem; line-height: 1.75; }
        .mdx-content h1, .mdx-content h2, .mdx-content h3 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 400;
          color: #1a1a1a;
          margin-top: 1.4rem;
          margin-bottom: 0.5rem;
        }
        .mdx-content h2 { font-size: 1.35rem; }
        .mdx-content h3 { font-size: 1.1rem; }
        .mdx-content strong { font-weight: 500; color: #333; }
        .mdx-content em { font-style: italic; color: #555; }
        .mdx-content ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 0.9rem; }
        .mdx-content ol { list-style: decimal; padding-left: 1.25rem; margin-bottom: 0.9rem; }
        .mdx-content li { margin-bottom: 0.3rem; font-size: 0.92rem; }
        .mdx-content a { color: #406580; text-decoration: underline; }
        .mdx-content blockquote {
          border-left: 2px solid rgba(64,101,128,0.35);
          padding-left: 1rem;
          color: #777;
          font-style: italic;
          margin: 1rem 0;
        }
        .mdx-content hr { border: none; border-top: 1px solid rgba(0,0,0,0.08); margin: 1.2rem 0; }
        .mdx-content table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
        .mdx-content th, .mdx-content td { border: 1px solid rgba(0,0,0,0.1); padding: 0.4rem 0.7rem; }
        .mdx-content th { background: rgba(64,101,128,0.06); font-weight: 500; }

        /* Below lg, sidebar always full width regardless of descriptionSize */
        @media (max-width: 1023px) {
          .event-details-sidebar { max-width: none !important; width: 100% !important; }
        }
      `}</style>

      <div className="event-page w-full min-h-screen bg-[#f7f6f3]">
        <Layout>
          {/* ── CINEMATIC HERO ── */}
          <div
            ref={heroRef}
            className="relative h-[70vh] md:h-[85vh] mt-16 overflow-hidden"
            style={{ background: "#0a1a24" }}
          >
            {/* Parallax background image */}
            <motion.div
              style={{ scale: heroScale }}
              className="absolute inset-0 origin-center"
            >
              {eventData.eventImagesUrls?.[0] &&
                (isVideoUrl(heroMedia) ? (
                  <video
                    src={heroMedia}
                    className="w-full h-full object-cover"
                    style={{ opacity: 0.55 }}
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={heroMedia}
                    alt={eventData.title}
                    className="w-full h-full object-cover"
                    style={{ opacity: 0.55 }}
                  />
                ))}
            </motion.div>

            {/* Gradient overlays */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(10,26,36,0.2) 0%, rgba(10,26,36,0.1) 40%, rgba(10,26,36,0.75) 100%)",
              }}
            />

            {/* Hero content */}
            <motion.div
              style={{ opacity: heroOpacity }}
              className="absolute inset-0 flex flex-col justify-end px-8 md:px-16 pb-14 md:pb-20"
            >
              {/* Category pill */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-5"
              >
                <span
                  className="hero-badge inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-white/80 text-xs tracking-widest uppercase"
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 400,
                    letterSpacing: "0.2em",
                  }}
                >
                  <FaCalendarAlt size={10} />
                  Evento
                  <span className="w-px h-3 bg-white/30 mx-1" />
                  <FaMapMarkerAlt size={10} />
                  Marrocco Art
                </span>
              </motion.div>

              {/* Title */}
              <motion.h1
                className="serif text-white leading-none mb-0"
                style={{
                  fontSize: "clamp(2.8rem, 7vw, 6.5rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.02em",
                  textShadow: "0 2px 40px rgba(0,0,0,0.4)",
                  maxWidth: "none",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 1,
                  delay: 0.4,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {eventData.title}
              </motion.h1>
            </motion.div>

            {/* Scroll indicator */}
            <motion.div
              className="absolute bottom-6 right-8 md:right-16 flex items-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <span
                className="text-white/40 text-xs tracking-widest uppercase"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Scorri
              </span>
              <motion.div
                className="w-px h-8 bg-white/30"
                animate={{ scaleY: [1, 0.4, 1] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          </div>

          {/* ── CONTENT AREA ── */}
          <div className="max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
              className="flex flex-col lg:flex-row gap-12 xl:gap-20"
            >
              {/* ── LEFT: DETAILS SIDEBAR ── */}
              {eventData.description && (
                <EventDetailsSidebar descriptionUrl={eventData.description} />
              )}

              {/* ── RIGHT: GALLERY ── */}
              <motion.div variants={fadeUp} className="flex-1 min-w-0">
                <div className="relative mb-8 flex items-end justify-between">
                  <div>
                    <p
                      className="text-xs tracking-[0.25em] uppercase text-[#406580]/70 mb-1"
                      style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Galleria
                    </p>
                    <h2
                      className="serif text-gray-900"
                      style={{ fontSize: "1.8rem", fontWeight: 400 }}
                    >
                      {eventData.title}
                    </h2>
                  </div>
                  <span
                    className="text-gray-400 text-sm"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 300,
                    }}
                  >
                    {eventData.eventImagesUrls?.length} immagini
                  </span>
                </div>

                <div className="divider-line mb-8" />

                <EventImages eventData={eventData} />
              </motion.div>
            </motion.div>

            {/* ── BACK BUTTON (mobile) ── */}
            <div className="mt-16 flex justify-center lg:hidden">
              <Link to="/eventi">
                <motion.button
                  whileHover={{ x: -4 }}
                  className="back-btn flex items-center gap-3 px-7 py-3 rounded-xl text-[#406580] text-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Torna agli eventi
                </motion.button>
              </Link>
            </div>
          </div>
        </Layout>
      </div>
    </>
  );
}

// Maps the frontmatter `descriptionSize` value to a Tailwind/inline width
const SIDEBAR_WIDTHS = {
  sm: "260px",
  md: "380px",
  lg: "520px",
  xl: "660px",
};

function EventDetailsSidebar({ descriptionUrl }) {
  const { content, meta, loading, error } = useMdxContent(descriptionUrl);

  const sidebarWidth =
    SIDEBAR_WIDTHS[meta.descriptionSize] || SIDEBAR_WIDTHS.md;

  if (loading) {
    return (
      <motion.aside
        variants={fadeUp}
        style={{ width: "100%", maxWidth: sidebarWidth }}
        className="shrink-0 lg:sticky lg:top-28 self-start"
      >
        <div className="detail-card rounded-2xl p-10 flex items-center justify-center min-h-[200px]">
          <div className="w-8 h-8 border border-[#406580]/30 border-t-[#406580] rounded-full animate-spin" />
        </div>
      </motion.aside>
    );
  }

  if (error || !content) return null;

  return (
    <motion.aside
      variants={fadeUp}
      className="event-details-sidebar w-full shrink-0 lg:sticky lg:top-28 self-start"
      style={{ maxWidth: sidebarWidth }}
    >
      <div className="detail-card rounded-2xl overflow-hidden">
        {/* Card header accent */}
        <div
          className="h-1 w-full"
          style={{ background: "linear-gradient(90deg, #406580, #6a9ab8)" }}
        />

        <div className="p-8 md:p-10">
          <div className="relative mb-8">
            <span className="chapter-number">01</span>
            <p
              className="text-xs tracking-[0.25em] uppercase text-[#406580]/70 mb-2"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
            >
              Dettagli
            </p>
            <h2
              className="serif text-gray-900"
              style={{ fontSize: "1.8rem", fontWeight: 400, lineHeight: 1.2 }}
            >
              {meta.title || "L'opera"}
            </h2>
          </div>

          <div className="divider-line mb-8" />

          <div
            className="mdx-content prose prose-sm max-w-none text-gray-600"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        </div>
      </div>

      {/* Back button — desktop */}
      <div className="mt-6 hidden lg:block">
        <Link to="/eventi">
          <motion.button
            whileHover={{ x: -4 }}
            className="back-btn flex items-center gap-3 px-5 py-3 rounded-xl text-[#406580] text-sm w-full justify-center"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Torna agli eventi
          </motion.button>
        </Link>
      </div>
    </motion.aside>
  );
}

function EventImages({ eventData }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      variants={stagger}
    >
      <ResponsiveMasonry
        columnsCountBreakPoints={{ 300: 1, 640: 2, 1024: 2, 1280: 3 }}
      >
        <Masonry gutter="12px">
          {eventData.eventImagesUrls.map((image, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="gallery-item overflow-hidden rounded-xl"
              style={{ boxShadow: "0 2px 20px rgba(0,0,0,0.08)" }}
            >
              {image === "lettera.jpg" ? (
                <Link to="/events/Documento-206.pdf" target="_blank">
                  <ImageWithOverlay image={image} index={index} />
                </Link>
              ) : image === "attestato.jpg" ? (
                <Link to="/events/Documento-205.pdf" target="_blank">
                  <ImageWithOverlay image={image} index={index} />
                </Link>
              ) : (
                <ImageWithOverlay image={image} index={index} />
              )}
            </motion.div>
          ))}
        </Masonry>
      </ResponsiveMasonry>
    </motion.div>
  );
}

function ImageWithOverlay({ image, index }) {
  const [isHovered, setIsHovered] = useState(false);
  const isVideo = isVideoUrl(image);
  const mediaSrc = image.startsWith("http")
    ? image
    : isVideo
      ? `https://res.cloudinary.com/dndm9hex2/video/upload/v1733332084/${image}`
      : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733332084/${image}`;

  // Deterministic heights to avoid layout shift on re-render
  const heights = [320, 400, 360, 440, 300, 380, 420, 340];
  const height = heights[index % heights.length];

  return (
    <div
      className="relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isVideo ? (
        <video
          src={mediaSrc}
          className="w-full object-cover"
          style={{ height: `${height}px` }}
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <img
          src={mediaSrc}
          alt={`Event image ${index + 1}`}
          className="w-full object-cover"
          style={{ height: `${height}px` }}
          loading="lazy"
        />
      )}

      {/* Hover overlay */}
      <motion.div
        initial={false}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0 flex items-end justify-between p-4"
        style={{
          background:
            "linear-gradient(to top, rgba(10,26,36,0.7) 0%, transparent 60%)",
        }}
      >
        <span
          className="text-white/90 text-xs tracking-widest uppercase"
          style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
        >
          {image === "lettera.jpg"
            ? "Visualizza lettera"
            : image === "attestato.jpg"
              ? "Visualizza attestato"
              : `${String(index + 1).padStart(2, "0")}`}
        </span>
        <div className="w-5 h-5 rounded-full border border-white/50 flex items-center justify-center">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path
              d="M1 7L7 1M7 1H2M7 1V6"
              stroke="white"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </motion.div>
    </div>
  );
}
